import React, { useEffect, useMemo, useState } from "react";
import {
  FaPlus,
  FaUserTie,
  FaUsers,
  FaCalendarAlt,
  FaSearch,
  FaMapSigns,
} from "react-icons/fa";
import { toast } from "react-toastify";

import Button from "../../../components/Button/Button";
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
} from "../../../components/Modal/Modal";
import { getAllActeursListWithGroup } from "./../../../services/acteursServices";
import { assignActorToEvent } from "./../../../services/acteureventServices";
import { sendNotification } from "../../../services/notificationServices";
import { getUserIdByActeurId } from "./../../../services/userServices";
import formatDate from "./../../../utils/formatDate";

const POSTES = [
  "Organisateur",
  "Responsable Ligue",
  "Responsable Comité",
  "Bénévolontaire",
  "Responsable Arbitrage",
  "Notateur",
  "Arbitre",
  "Commissaire Sportif",
  "Juge",
];

const TAPS_REQUIRED_POSTES = ["Arbitre", "Commissaire Sportif"];

const normalize = (value) =>
  (value || "")
    .toString()
    .normalize("NFD")
    .replace(/\p{Diacritic}/gu, "")
    .toLowerCase()
    .trim();

const formatTapisValue = (value) => {
  const cleaned = (value || "").toString().trim();
  if (!cleaned) return "";
  if (/^\d+$/.test(cleaned)) return `Tapis ${cleaned}`;
  return cleaned;
};

const ActorSelectionModal = ({
  isOpen,
  onClose,
  eventId,
  eventTitle,
  eventStart,
  refreshList,
}) => {
  const [acteurs, setActeurs] = useState([]);
  const [selectedActeurId, setSelectedActeurId] = useState("");
  const [poste, setPoste] = useState("");
  const [tapis, setTapis] = useState("");
  const [filter, setFilter] = useState("");
  const [loadingActeurs, setLoadingActeurs] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const titleId = "actor-selection-modal-title";
  const descriptionId = "actor-selection-modal-description";
  const needsTapis = TAPS_REQUIRED_POSTES.includes(poste);

  useEffect(() => {
    if (isOpen) {
      fetchActeurs();
      setSelectedActeurId("");
      setPoste("");
      setTapis("");
      setFilter("");
    }
  }, [isOpen]);

  useEffect(() => {
    if (!needsTapis) {
      setTapis("");
    }
  }, [needsTapis]);

  const fetchActeurs = async () => {
    try {
      setLoadingActeurs(true);
      const data = await getAllActeursListWithGroup();

      const sorted = (data || []).sort((a, b) =>
        `${a.nom} ${a.prenom}`
          .toLowerCase()
          .localeCompare(`${b.nom} ${b.prenom}`.toLowerCase()),
      );

      setActeurs(sorted);
    } catch (error) {
      console.error("Erreur lors du chargement des acteurs", error);
      toast.error("Erreur lors du chargement des acteurs.");
      setActeurs([]);
    } finally {
      setLoadingActeurs(false);
    }
  };

  const filteredActeurs = useMemo(() => {
    if (!filter) return acteurs;

    const search = normalize(filter);

    return acteurs.filter((a) => {
      const nomPrenom = normalize(`${a.nom} ${a.prenom}`);
      const club = normalize(a.club_acteur);
      const groups = normalize((a.Groups || []).map((g) => g.nom).join(" "));
      return (
        nomPrenom.includes(search) ||
        club.includes(search) ||
        groups.includes(search)
      );
    });
  }, [acteurs, filter]);

  const selectedActeur = useMemo(() => {
    return (
      filteredActeurs.find((a) => String(a.id) === String(selectedActeurId)) ||
      acteurs.find((a) => String(a.id) === String(selectedActeurId)) ||
      null
    );
  }, [filteredActeurs, acteurs, selectedActeurId]);

  const handleAssign = async () => {
    if (!poste) {
      toast.error("Merci de sélectionner un poste.");
      return;
    }

    if (!selectedActeurId) {
      toast.error("Merci de sélectionner un acteur.");
      return;
    }

    if (needsTapis && !tapis.trim()) {
      toast.error("Merci de renseigner le tapis.");
      return;
    }

    try {
      setSubmitting(true);

      const formattedTapis = needsTapis ? formatTapisValue(tapis) : null;

      await assignActorToEvent(selectedActeurId, eventId, poste, formattedTapis);
      toast.success("Acteur inscrit avec succès.");

      const users = await getUserIdByActeurId(selectedActeurId);

      const message = `Le Responsable de la Manifestation ou le Responsable d'arbitrage vous a convoqué sur l'événement : "${eventTitle}" le ${formatDate(
        eventStart,
      )} en tant que ${poste}${
        formattedTapis ? ` (${formattedTapis})` : ""
      }.`;

      for (const user of users || []) {
        await sendNotification(message, user.user_id);
      }

      await refreshList?.();
      onClose?.();
    } catch (error) {
      console.error(
        "Erreur lors de l'inscription de l'acteur à l'événement",
        error,
      );
      toast.error("Erreur lors de l'inscription de l'acteur à l'événement.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      size="xl"
      labelledById={titleId}
      describedById={descriptionId}
      initialFocusSelector='input[name="actor-filter"]'
    >
      <ModalHeader
        id={titleId}
        title="Inscrire un acteur"
        subtitle="Ajout d’une convocation sur l’événement"
        onClose={onClose}
      />

      <ModalBody id={descriptionId}>
        <div className="space-y-5">
          <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4 dark:border-slate-700 dark:bg-slate-800/70">
            <div className="flex items-start gap-3">
              <div className="mt-0.5 flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-blue-100 text-blue-700 dark:bg-blue-500/15 dark:text-blue-300">
                <FaCalendarAlt />
              </div>

              <div className="min-w-0 flex-1">
                <p className="text-[11px] uppercase tracking-[0.18em] text-slate-500 dark:text-slate-400">
                  Événement
                </p>

                <div className="mt-1 text-base font-semibold text-slate-900 dark:text-white">
                  {eventTitle || "Événement non trouvé"}
                </div>

                {eventStart && (
                  <div className="mt-1 text-sm text-slate-600 dark:text-slate-300">
                    Date :{" "}
                    <span className="font-medium">{formatDate(eventStart)}</span>
                  </div>
                )}

                <p className="mt-2 text-xs italic text-slate-500 dark:text-slate-400">
                  Sélectionnez le poste et l’acteur à convoquer. Une notification
                  sera envoyée automatiquement.
                </p>
              </div>
            </div>
          </div>

          <div>
            <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wide text-slate-700 dark:text-slate-200">
              Poste sur l’événement
            </label>

            <div className="relative">
              <FaUserTie className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <select
                value={poste}
                onChange={(e) => setPoste(e.target.value)}
                className="h-11 w-full rounded-2xl border border-slate-300 bg-white pl-10 pr-4 text-sm text-slate-900 outline-none transition focus:border-indigo-500 focus:ring-4 focus:ring-indigo-500/10 dark:border-slate-700 dark:bg-slate-950 dark:text-white"
              >
                <option value="">Sélectionner un poste</option>
                {POSTES.map((item) => (
                  <option key={item} value={item}>
                    {item}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {needsTapis && (
            <div>
              <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wide text-slate-700 dark:text-slate-200">
                Tapis
              </label>

              <div className="relative">
                <FaMapSigns className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                <input
                  type="text"
                  value={tapis}
                  onChange={(e) => setTapis(e.target.value)}
                  placeholder="Ex : 1, 2, 3 ou Tapis 1"
                  className="h-11 w-full rounded-2xl border border-slate-300 bg-white pl-10 pr-4 text-sm text-slate-900 outline-none transition focus:border-indigo-500 focus:ring-4 focus:ring-indigo-500/10 dark:border-slate-700 dark:bg-slate-950 dark:text-white"
                />
              </div>

              <div className="mt-1 text-[11px] text-slate-500 dark:text-slate-400">
                Recommandé : saisir simplement 1, 2, 3… le libellé sera
                normalisé automatiquement.
              </div>
            </div>
          )}

          <div>
            <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wide text-slate-700 dark:text-slate-200">
              Filtrer les acteurs
            </label>

            <div className="relative">
              <FaSearch className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <input
                data-autofocus
                name="actor-filter"
                type="text"
                value={filter}
                onChange={(e) => setFilter(e.target.value)}
                placeholder="Nom, prénom, club, groupe…"
                className="h-11 w-full rounded-2xl border border-slate-300 bg-white pl-10 pr-4 text-sm text-slate-900 outline-none transition focus:border-indigo-500 focus:ring-4 focus:ring-indigo-500/10 dark:border-slate-700 dark:bg-slate-950 dark:text-white"
              />
            </div>
          </div>

          <div>
            <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wide text-slate-700 dark:text-slate-200">
              Sélectionner un acteur
            </label>

            <select
              value={selectedActeurId}
              onChange={(e) => setSelectedActeurId(e.target.value)}
              className="min-h-[44px] w-full rounded-2xl border border-slate-300 bg-white px-4 py-2 text-sm text-slate-900 outline-none transition focus:border-indigo-500 focus:ring-4 focus:ring-indigo-500/10 dark:border-slate-700 dark:bg-slate-950 dark:text-white"
            >
              <option value="">Sélectionner un acteur</option>

              {filteredActeurs.map((acteur) => (
                <option key={acteur.id} value={acteur.id}>
                  {acteur.nom?.toUpperCase()} {acteur.prenom}
                  {acteur.club_acteur ? ` - ${acteur.club_acteur}` : ""}
                  {(acteur.Groups || []).length > 0
                    ? ` [${acteur.Groups.map((g) => g.nom).join(", ")}]`
                    : ""}
                </option>
              ))}
            </select>

            <div className="mt-2 flex flex-wrap items-center gap-2 text-[11px] text-slate-500 dark:text-slate-300">
              <span className="inline-flex items-center gap-1 rounded-full bg-slate-100 px-2.5 py-1 dark:bg-slate-800">
                <FaUsers />
                {loadingActeurs
                  ? "Chargement des acteurs…"
                  : `${filteredActeurs.length} acteur(s) disponible(s)`}
              </span>

              {selectedActeur && (
                <span className="inline-flex items-center rounded-full bg-indigo-50 px-2.5 py-1 text-indigo-700 dark:bg-indigo-500/10 dark:text-indigo-300">
                  Sélection : {selectedActeur.nom?.toUpperCase()}{" "}
                  {selectedActeur.prenom}
                </span>
              )}
            </div>
          </div>

          {selectedActeur && (
            <div className="rounded-2xl border border-indigo-200 bg-indigo-50/60 p-4 dark:border-indigo-500/20 dark:bg-indigo-500/10">
              <p className="text-[11px] uppercase tracking-[0.18em] text-indigo-700 dark:text-indigo-300">
                Aperçu de la convocation
              </p>

              <div className="mt-2 text-sm text-slate-800 dark:text-slate-100">
                <span className="font-semibold">
                  {selectedActeur.nom?.toUpperCase()} {selectedActeur.prenom}
                </span>
                {poste ? (
                  <>
                    {" "}
                    sera convoqué en tant que{" "}
                    <span className="font-semibold">{poste}</span>
                    {needsTapis && tapis.trim() ? (
                      <>
                        {" "}
                        sur <span className="font-semibold">{formatTapisValue(tapis)}</span>
                      </>
                    ) : null}
                  </>
                ) : (
                  <> a bien été sélectionné.</>
                )}
              </div>

              {selectedActeur.club_acteur && (
                <div className="mt-1 text-xs text-slate-600 dark:text-slate-300">
                  Club : {selectedActeur.club_acteur}
                </div>
              )}

              {(selectedActeur.Groups || []).length > 0 && (
                <div className="mt-1 text-xs text-slate-600 dark:text-slate-300">
                  Groupes :{" "}
                  {selectedActeur.Groups.map((g) => g.nom).join(", ")}
                </div>
              )}
            </div>
          )}
        </div>
      </ModalBody>

      <ModalFooter>
        <Button
          onClick={onClose}
          className="!rounded-2xl !bg-slate-100 !text-slate-800 hover:!bg-slate-200 dark:!bg-slate-800 dark:!text-slate-100 dark:hover:!bg-slate-700"
          text="Fermer"
        />

        <Button
          onClick={handleAssign}
          className="!rounded-2xl"
          text={submitting ? "Inscription..." : "Inscrire"}
          icon={FaPlus}
          disabled={submitting || loadingActeurs}
        />
      </ModalFooter>
    </Modal>
  );
};

export default ActorSelectionModal;